/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './EventSubscription';

const EventSubscriptionManager: ComponentManagerFn<'EventSubscription'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'EventSubscription'>(platform, 'EventSubscription'),
    type: 'EventSubscription',
    name: 'Event Subscription',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnSubscriptionEvent'] as ItemType[]).includes(childType),
    defaultChildren: ['TriggerOnSubscriptionEvent'],
    defaultValue,
    props: {
      context: {
        label: 'Context',
        type: 'expression',
        returnType: async () => 'string',
        placeholder: 'e.g. Order',
        helpText:
          'Specifiy the Context to which the event subscription to be created. Will only listen to the events that are published with this context.',
      },
      contextId: {
        label: 'Context ID',
        type: 'expression',
        returnType: async () => 'string',
        placeholder: 'e.g. #{OrderDS.orderId}',
        helpText:
          'Specifiy the Context ID that will be used to further narow down the events. Will only listen to the events that are published with this Context ID.',
      },
      // timeStamp: {
      //   label: 'Timestamp',
      //   type: 'expression',
      //   returnType: () => 'string',
      //   placeholder: 'e.g. #{OrderDS.lastUpdateDate}',
      //   helpText:
      //     'An expression that must return an ISO format date string. Any events received prior to this date and time will be ignored',
      // }
    },
    getDisplayText: (item) => item.props.context.value,
    category: 'Others',
    icon: { iconName: 'bolt' },
    noOverlay: true,
    description: `Event Subscription can be used to listen for business events in realtime and perform any action when such event is received.

**Use Cases**
- Updating Metric Box values on the Dashboard as the value changes due to the transactions performed by users in realtime.
- Displaying realtime Inventory Onhand for the Item during Order Line Entry.
- Display an alert when two or more users try to work on a same task.
- Auto refresh a chart when the dataset get's updated on the server

\`\`\`alert_warning
Note: Subscription will be active only when the parent component is mounted on the page. If a Subscription is added to a component inside a tab that is not currently active, it will not be listening to any events unless the user navigates to that tab.

If you want the Subscription to be always active within a page, add it to the Page Component.
\`\`\`

**Ways to Publish Events**
- From Client: Use \`Publish Event\` action
- From Server: Use db.publish() e.g. \`await db.publish(context, contextId, {someValue: 100});\`

**How it works**
- \`Event Subscription\` will listen to the events that are published by the server.
- The events are published by the server (or from the client with \`Publish Event\` action) when a business transaction is performed by the user.
- The events are published with a Context and Context ID.
- The \`Event Subscription\` will listen to the events that are published with the Context and Context ID specified in the Event Subscription.
- The \`Event Subscription\` will trigger the actions specified in the child \`Trigger On Subscription Event\` trigger when an event is received.
`,
  };
};

export default EventSubscriptionManager;
